import {observer} from 'mobx-react-lite'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {ExtensionTag, getExtensionTagTitle} from 'yougile-model'
import {ExtensionClient} from '@client/extension-client'
import {Tracking, TrackingEvent} from '@common/tracking'
import {OptionsItem} from '@components/common/base/dropdown-button-menu/types'
import {LoaderIcon} from '@components/common/base/loader-new'
import {Popup} from '@components/common/base/popup'
import {TabData} from '@components/common/base/tabs'
import {useLoggedInStore} from '@components/logged-in/logged-in-store'
import {MessageBox} from '@components/message-box'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {
  getSortingOptions,
  sortingOptionMostCommentedKey,
  sortingOptionMostInstalledKey,
  sortingOptionReleaseDate,
  sortingOptionTopRatedKey
} from './constants'
import {ExtensionsGalleryMain} from './extensions-gallery-main'
import {ExtensionGallerySidebar} from './extensions-gallery-sidebar'
import {extensionsStore, ExtensionStore} from './extensions-store'

const TwMainLoader = tw.div`
  h-full
  w-full
  flex
  items-center
  justify-center
`

type ExtensionsGalleryPopupProps = {
  onClose: () => void
}

export type FilterId = 'all' | 'enabled' | 'new' | ExtensionTag

export interface Filter extends TabData {
  id: FilterId
}

const extensionSearchFilter = (extension: ExtensionStore, pattern: string) => {
  return (
    extension.name.toLowerCase().includes(pattern) ||
    extension.description.toLowerCase().includes(pattern) ||
    extension.keywords.find(k => k.toLowerCase().includes(pattern)) !==
      undefined ||
    extension.tags.find(k =>
      getExtensionTagTitle(k).toLowerCase().includes(pattern)
    ) !== undefined ||
    Lang.msg.extensions.gallery.filters.enabled.toLowerCase().includes(pattern)
  )
}

export const ExtensionsGalleryPopup = observer(
  // eslint-disable-next-line max-lines-per-function
  ({onClose}: ExtensionsGalleryPopupProps) => {
    const [openedDetails, setOpenedDetails] =
      useState<ExtensionStore>(undefined)
    const [openedSettings, setOpenedSettings] =
      useState<ExtensionStore>(undefined)

    const [searchQuery, setSearchQuery] = useState('')
    const mainContainerRef = useRef<HTMLElement>(null)
    const sidebarContainerRef = useRef<HTMLElement>(null)
    const momerizedScrollPosition = useRef<number>(0)
    const [loggedInStore] = useLoggedInStore()

    const [fetchInProgress, setFetchInProgress] = useState(
      extensionsStore.extensions.size === 0
    )

    const handlePageNavigation = () => {
      const extInPath = [...extensionsStore.extensions.values()].find(
        ext => ext.id === loggedInStore.subsubpage
      )
      setOpenedDetails(extInPath)
    }

    const fetchAndInitExtensions = async () => {
      await ExtensionClient.list(globals.getCompanyId())
        .then(res => {
          extensionsStore.setExtensions(res)
          handlePageNavigation()
        })
        .catch(e => {
          logger.error(e)
          MessageBox.show(Lang.msg.extensions.loadError, 'danger')
        })

      setFetchInProgress(false)
    }

    useEffect(() => {
      fetchAndInitExtensions()
    }, [])

    useEffect(() => handlePageNavigation, [loggedInStore.subsubpage])

    const isSearchActive = searchQuery.trim().length > 0
    const isSidebarDisabled = openedSettings !== undefined

    const threeMonthsAgo = new Date()
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)

    const availableTags = new Set<ExtensionTag>()
    const installedExtensions = []
    const newExtensions = []

    const list: ExtensionStore[] = useMemo(
      () => [...extensionsStore.extensions.values()],
      [extensionsStore.state.modified]
    )

    for (const extension of list) {
      for (const tag of extension.tags) {
        availableTags.add(tag)
      }

      if (extension.enabled) {
        installedExtensions.push(extension)
      }

      if (extension.dateReleased && extension.dateReleased >= threeMonthsAgo) {
        newExtensions.push(extension)
      }
    }

    const filters: Filter[] = []

    if (installedExtensions.length) {
      filters.push({
        id: 'enabled',
        title: Lang.msg.extensions.gallery.filters.enabled
      })
    }

    filters.push({id: 'all', title: Lang.msg.extensions.gallery.filters.all})

    if (newExtensions.length > 0) {
      filters.push({id: 'new', title: Lang.msg.extensions.gallery.filters.new})
    }

    filters.push(
      ...(Object.keys(Lang.msg.extensions.tags) as ExtensionTag[])
        .filter(tag => availableTags.has(tag))
        .map(tag => ({
          id: tag,
          title: getExtensionTagTitle(tag)
        }))
    )

    const getFilter = (id: FilterId): Filter | undefined =>
      filters.filter(f => f.id === id)?.[0]

    const [activeFilter, setActiveFilter] = useState<Filter | undefined>(
      getFilter('all')
    )

    const sortingOptions = getSortingOptions()
    const defaultSortingOption = sortingOptions[3]
    const [activeSortingOption, setActiveSortingOption] =
      useState(defaultSortingOption)

    // NOTE: this use-effect covers case when user disable last "enabled extension",
    // and filter "Enabled extensions" disappears from left sidebar.
    useEffect(() => {
      if (installedExtensions.length === 0 && activeFilter.id === 'enabled') {
        setActiveFilter(getFilter('all'))
      }
    }, [installedExtensions.length])

    let filteredList = list

    if (searchQuery) {
      const pattern = searchQuery.toLowerCase().trim()
      filteredList = list.filter(e => extensionSearchFilter(e, pattern))
    } else if (activeFilter) {
      if (activeFilter.id === 'enabled') {
        filteredList = list.filter(e => e.enabled)
      } else if (activeFilter.id === 'new') {
        filteredList = newExtensions
      } else if (activeFilter.id !== 'all') {
        filteredList = list.filter(e =>
          e.tags.includes(activeFilter.id as ExtensionTag)
        )
      }
    }

    const filteredSortedList = useMemo(() => {
      const getSortValue = {
        [sortingOptionMostInstalledKey]: (e: ExtensionStore) => e.installsCount,
        [sortingOptionTopRatedKey]: (e: ExtensionStore) => e.rating,
        [sortingOptionMostCommentedKey]: (e: ExtensionStore) => e.commentsCount,
        [sortingOptionReleaseDate]: (e: ExtensionStore) => e.dateReleased
      }[activeSortingOption.key]

      return [...filteredList].sort((a, b) => {
        const aValue = getSortValue?.(a)
        const bValue = getSortValue?.(b)

        const isAValid = aValue !== undefined && aValue !== null
        const isBValid = bValue !== undefined && bValue !== null

        if (!isAValid && !isBValid) {
          return a.name.localeCompare(b.name)
        }
        if (!isAValid) {
          return 1
        }
        if (!isBValid) {
          return -1
        }

        const diff = Number(bValue) - Number(aValue)
        return diff !== 0 ? diff : a.name.localeCompare(b.name)
      })
    }, [filteredList, activeSortingOption])

    const searchResultsCount = filteredSortedList.length
    const searchResultsTitle = `${Lang.msg.searchResults} ${
      searchResultsCount > 0 ? `(${searchResultsCount})` : ''
    }`

    const onSearchChanged = (text: string) => {
      mainContainerRef.current?.scrollTo({top: 0})
      sidebarContainerRef.current?.scrollTo({top: 0})
      setActiveSortingOption(defaultSortingOption)
      setActiveFilter(getFilter('all'))
      setSearchQuery(text)
    }

    const onFilterChanged = (id: FilterId) => {
      setActiveFilter(filters.find(f => f.id === id))
      const isNew = id === 'new'
      setActiveSortingOption(sortingOptions[isNew ? 3 : 0])
      mainContainerRef.current?.scrollTo({top: 0})
      sidebarContainerRef.current?.scrollTo({top: 0})
    }

    const onOpenSettings = (ext: ExtensionStore) => {
      sidebarContainerRef.current?.scrollTo({top: 0})
      mainContainerRef.current?.scrollTo({top: 0})
      setOpenedSettings(ext)
    }

    const onOpenDetails = (ext: ExtensionStore) => {
      sidebarContainerRef.current?.scrollTo({top: 0})
      momerizedScrollPosition.current = mainContainerRef.current?.scrollTop
      setOpenedDetails(ext)
      PageService.goToExtensions(ext.id)
    }

    const onClickScrollToTop = () => {
      requestAnimationFrame(() => {
        mainContainerRef.current?.scrollTo({
          top: 0

          // NOTE: Doesn't work with PerfectScrollbar because it re-renders the list
          // during the animation, which makes it look like only 1px was scrolled.
          // behavior: 'smooth'
        })
      })
    }

    const onCloseDetails = () => {
      setOpenedDetails(undefined)
      PageService.goToExtensions()
      sidebarContainerRef.current?.scrollTo({top: 0})
      setTimeout(() => {
        if (momerizedScrollPosition.current) {
          mainContainerRef.current?.scrollTo({
            top: momerizedScrollPosition.current
          })
        }
      }, 0)
    }

    const onSortingOptionsChanged = (option: OptionsItem) => {
      const nextSortingOption =
        sortingOptions.find(o => o.key === option.key) ?? defaultSortingOption

      setActiveSortingOption(nextSortingOption)

      Tracking.send(TrackingEvent.extensionsOnSortingChanged, {
        type: nextSortingOption.key
      })
    }

    return (
      <Popup
        width={896}
        title={Lang.msg.extensions.gallery.title}
        close={onClose}
        noScroll={fetchInProgress || Boolean(openedDetails)}
        containerRef={mainContainerRef}
        contentPaddings={0}
        dividerVisibility="visible"
        sidebar={
          !fetchInProgress && (
            <ExtensionGallerySidebar
              containerRef={sidebarContainerRef}
              openedDetails={openedDetails}
              isSearchActive={isSearchActive}
              isSidebarDisabled={isSidebarDisabled}
              onResetSearch={() => setSearchQuery('')}
              searchQuery={searchQuery}
              activeFilter={activeFilter}
              filters={filters}
              onOpenSettings={onOpenSettings}
              onSearchChanged={onSearchChanged}
              onFilterChanged={onFilterChanged}
            />
          )
        }
      >
        {fetchInProgress ? (
          <TwMainLoader>
            <LoaderIcon />
          </TwMainLoader>
        ) : (
          <ExtensionsGalleryMain
            openedDetails={openedDetails}
            onCloseDetails={onCloseDetails}
            onCloseSettings={() => setOpenedSettings(undefined)}
            onOpenDetails={onOpenDetails}
            onOpenSettings={onOpenSettings}
            onSortingOptionsChanged={onSortingOptionsChanged}
            activeSortingOption={activeSortingOption}
            activeFilter={activeFilter}
            filteredList={filteredSortedList}
            openedSettings={openedSettings}
            isSearchActive={isSearchActive}
            searchResultsTitle={searchResultsTitle}
            onClickScrollToTop={onClickScrollToTop}
          />
        )}
      </Popup>
    )
  }
)
